"use client";

import { useTranslations } from "next-intl";

import { MultiSelectField } from "@/components/ui/multiSelectField";
import { SelectField, SelectItem } from "@/components/ui/select";
import type { Site, Standard } from "@/types";
import type { AssignedProject } from "@/types/project";

export interface FilterAssetOption {
  value: string;
  label: string;
}

interface FilterScopeFieldsProps {
  standards: Standard[] | undefined;
  assignedProjects: AssignedProject[] | undefined;
  siteOptions: Site[];
  assetOptions: FilterAssetOption[];
  selectedStandard: string;
  selectedProject: string;
  selectedSite: string;
  selectedAssets: string[];
  isProjectSelected: boolean;
  onStandardChange: (value: string) => void;
  onProjectChange: (value: string) => void;
  onSiteChange: (value: string) => void;
  onAssetsChange: (value: string[]) => void;
  sitePlaceholder?: string;
  assetPlaceholder?: string;
}

export function FilterScopeFields({
  standards,
  assignedProjects,
  siteOptions,
  assetOptions,
  selectedStandard,
  selectedProject,
  selectedSite,
  selectedAssets,
  isProjectSelected,
  onStandardChange,
  onProjectChange,
  onSiteChange,
  onAssetsChange,
  sitePlaceholder,
  assetPlaceholder,
}: FilterScopeFieldsProps) {
  const t = useTranslations("home");
  const selectPlaceholder = t("scope.select");

  return (
    <>
      <div className="flex flex-col gap-4 border-b border-neutral-100 pb-6">
        <SelectField
          label={t("scope.standard")}
          placeholder={selectPlaceholder}
          value={selectedStandard}
          onValueChange={onStandardChange}
        >
          {standards?.map((standard) => (
            <SelectItem key={standard.id} value={standard.name}>
              {standard.name}
            </SelectItem>
          ))}
        </SelectField>
      </div>

      <div className="flex flex-col gap-4 border-b border-neutral-100 pb-6">
        <SelectField
          label={t("scope.assignedProject")}
          placeholder={selectPlaceholder}
          id="assigned_projects"
          value={selectedProject}
          onValueChange={onProjectChange}
        >
          {assignedProjects?.length === 0 ? (
            <SelectItem value="_">{t("scope.noProjects")}</SelectItem>
          ) : (
            assignedProjects?.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.code ?? project.name}
              </SelectItem>
            ))
          )}
        </SelectField>

        <div>
          <SelectField
            label={t("scope.site")}
            placeholder={sitePlaceholder ?? selectPlaceholder}
            disabled={!isProjectSelected}
            value={selectedSite}
            onValueChange={onSiteChange}
          >
            {siteOptions.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.name}
              </SelectItem>
            ))}
          </SelectField>
        </div>

        <div>
          <MultiSelectField
            label={t("scope.asset")}
            placeholder={assetPlaceholder ?? selectPlaceholder}
            disabled={!isProjectSelected}
            options={assetOptions}
            value={selectedAssets}
            onValueChange={onAssetsChange}
          />
        </div>
      </div>
    </>
  );
}
